import React, { useRef, useState } from 'react';
import { FiUpload, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { FormAlert } from './form';
import { formatMoney } from '../utils/money';

const BankReconciliation = ({ modoOscuro, onApplied, onCancel }) => {
  const { secureFetch } = useApi();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [parseErrors, setParseErrors] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [csvRaw, setCsvRaw] = useState('');
  const [needsMapping, setNeedsMapping] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [fields, setFields] = useState([]);
  const [columnMap, setColumnMap] = useState({});

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const inputClass = modoOscuro ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300';

  const previewWithMap = async (csv, map) => {
    setError('');
    setLoading(true);
    try {
      const res = await secureFetch('/api/reconciliation/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv, columnMap: map }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al leer el extracto');

      if (data.needsMapping) {
        setNeedsMapping(true);
        setHeaders(data.headers || []);
        setFields(data.fields || []);
        setColumnMap(data.suggestedMap || {});
        setSuggestions([]);
        setStats(null);
        setParseErrors(data.parseErrors || []);
        return;
      }

      setNeedsMapping(false);
      setSuggestions(
        (data.suggestions || []).map((s) => ({
          ...s,
          selected: Boolean(s.targetId && s.score >= 60),
        }))
      );
      setStats(data.stats || null);
      setParseErrors(data.parseErrors || []);
    } catch (err) {
      setError(err.message);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const csv = await file.text();
    setCsvRaw(csv);
    await previewWithMap(csv, null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const applyMapping = async () => {
    const hasAmount = columnMap.monto != null || columnMap.debito != null || columnMap.credito != null;
    if (columnMap.fecha == null || !hasAmount) {
      setError('Asigná al menos Fecha y Monto (o Débito/Crédito).');
      return;
    }
    await previewWithMap(csvRaw, columnMap);
  };

  const toggleItem = (movementId) => {
    setSuggestions((prev) => prev.map((s) => (
      s.movementId === movementId ? { ...s, selected: !s.selected } : s
    )));
  };

  const handleApply = async () => {
    const selected = suggestions.filter((s) => s.selected && s.targetId);
    if (!selected.length) {
      setError('Seleccioná al menos una conciliación sugerida.');
      return;
    }
    setApplying(true);
    setError('');
    try {
      const res = await secureFetch('/api/reconciliation/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: selected }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al conciliar');
      onApplied?.(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`}>
      <div className={`px-4 py-3 border-b ${divide}`}>
        <h2 className={`text-sm font-semibold ${textPri}`}>Conciliación bancaria lite</h2>
        <p className={`text-xs mt-0.5 ${textMuted}`}>
          Importá un extracto CSV y conciliá movimientos con ventas impagas o egresos existentes.
        </p>
      </div>

      <div className="p-4 space-y-4">
        <FormAlert type="error">{error || null}</FormAlert>

        <label className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 cursor-pointer ${modoOscuro ? 'border-gray-600 hover:border-[#5CA3FF]' : 'border-gray-300 hover:border-[#004AAD]'}`}>
          <FiUpload className={`w-8 h-8 ${textMuted}`} />
          <span className={`text-sm font-medium ${textPri}`}>Subir extracto bancario (CSV)</span>
          <span className={`text-xs ${textMuted}`}>Fecha, concepto y monto o débito/crédito</span>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
        </label>

        {needsMapping && headers.length > 0 && (
          <div className={`rounded-xl border p-4 space-y-3 ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-sm font-medium ${textPri}`}>Mapeá las columnas del extracto</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.id}>
                  <label className={`block text-xs mb-1 ${textMuted}`}>{f.label}</label>
                  <select
                    value={columnMap[f.id] ?? ''}
                    onChange={(e) => setColumnMap((p) => ({
                      ...p,
                      [f.id]: e.target.value === '' ? undefined : Number(e.target.value),
                    }))}
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${inputClass}`}
                  >
                    <option value="">— Sin asignar —</option>
                    {headers.map((h, idx) => (
                      <option key={idx} value={idx}>{h || `Columna ${idx + 1}`}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={applyMapping}
              disabled={loading}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
            >
              Analizar extracto
            </button>
          </div>
        )}

        {loading && <p className={`text-sm ${textMuted}`}>Analizando movimientos…</p>}

        {stats && (
          <div className={`grid grid-cols-3 gap-2 text-center text-xs ${textSec}`}>
            <div className={`rounded-lg p-2 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="font-semibold">{stats.movements}</div>
              <div className={textMuted}>Movimientos</div>
            </div>
            <div className={`rounded-lg p-2 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="font-semibold text-green-500">{stats.matched}</div>
              <div className={textMuted}>Sugeridos</div>
            </div>
            <div className={`rounded-lg p-2 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="font-semibold text-amber-500">{stats.unmatched}</div>
              <div className={textMuted}>Sin match</div>
            </div>
          </div>
        )}

        {parseErrors.length > 0 && (
          <ul className={`text-xs space-y-1 ${textMuted}`}>
            {parseErrors.slice(0, 5).map((msg) => (
              <li key={msg} className="flex items-start gap-1">
                <FiAlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {msg}
              </li>
            ))}
          </ul>
        )}

        {suggestions.length > 0 && (
          <div className={`rounded-xl border overflow-hidden ${divide}`}>
            <ul className={`divide-y ${divide}`}>
              {suggestions.map((s) => (
                <li key={s.movementId} className={`px-3 py-2.5 flex items-start gap-3 ${modoOscuro ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}>
                  {s.targetId ? (
                    <input
                      type="checkbox"
                      checked={!!s.selected}
                      onChange={() => toggleItem(s.movementId)}
                      className="mt-1 rounded"
                    />
                  ) : (
                    <span className="w-4 mt-1" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-medium ${textPri}`}>
                      {s.fecha} · {formatMoney(s.amount, 'ARS')}
                      {s.movement?.direction === 'credit' ? ' (ingreso)' : ' (egreso)'}
                    </div>
                    <div className={`text-xs truncate ${textMuted}`}>{s.concepto || 'Sin concepto'}</div>
                    {s.targetLabel ? (
                      <div className={`text-xs mt-1 flex items-center gap-1 ${modoOscuro ? 'text-green-400' : 'text-green-700'}`}>
                        <FiCheck className="w-3.5 h-3.5" />
                        {s.targetLabel} · confianza {s.score}%
                      </div>
                    ) : (
                      <div className={`text-xs mt-1 ${textMuted}`}>Sin coincidencia automática</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {suggestions.some((s) => s.selected) && (
            <button
              type="button"
              onClick={handleApply}
              disabled={applying}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
            >
              {applying ? 'Conciliando…' : 'Aplicar seleccionadas'}
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border ${modoOscuro ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}
            >
              Volver
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default BankReconciliation;
